import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SessionService } from '../../services/session.service';
import { ApiService } from '../../services/api.service';
import {
  SessionResult,
  StaticWeatherConfig,
  StaticWeatherSkies,
  StaticWeatherTrackMoisture,
  StaticWeatherWindDirection,
} from '@spectrace/shared';

const DEFAULT_STATIC_WEATHER_CONFIG: StaticWeatherConfig = {
  skies: 'partly_cloudy',
  trackMoisture: 'none',
  airTempC: 25.5,
  relativeHumidityPct: 55,
  windSpeedMps: 2,
  windDirection: 'north',
};

@Component({
  selector: 'app-edit-session-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './edit-session-modal.component.html',
  styleUrls: ['./edit-session-modal.component.css'],
})
export class EditSessionModalComponent implements OnInit {
  @Input() session!: SessionResult;
  @Output() close = new EventEmitter<void>();
  @Output() sessionUpdated = new EventEmitter<void>();

  selectedTrackId: number | null = null;
  lapsPerDriver = 10;
  enforceStaticWeather = false;
  staticWeatherSkies: StaticWeatherSkies = 'clear';
  staticTrackMoisture: StaticWeatherTrackMoisture = 'none';
  staticAirTempC?: number;
  staticRelativeHumidityPct?: number;
  staticWindSpeedMps?: number;
  staticWindDirection: StaticWeatherWindDirection = 'north';
  minIRating?: number;
  maxIRating?: number;
  selectedCarClasses: string[] = [];
  startTime = '';
  endTime = '';
  error = '';
  saving = false;

  // Track selection with typeahead
  tracks: any[] = [];
  filteredTracks: any[] = [];
  trackSearchQuery = '';
  showTrackDropdown = false;

  // Car class selection (multiselect)
  availableCarClasses: any[] = [];
  availableCars: any[] = [];
  carSearchQuery = '';
  filteredCars: any[] = [];
  showCarDropdown = false;

  // Available laps per driver options (5-50 laps)
  readonly lapsPerDriverOptions = [5, 10, 15, 20, 25, 30, 40, 50];
  readonly staticWeatherSkyOptions: Array<{
    value: StaticWeatherSkies;
    label: string;
  }> = [
    { value: 'clear', label: 'Clear' },
    { value: 'partly_cloudy', label: 'Partly Cloudy' },
    { value: 'mostly_cloudy', label: 'Mostly Cloudy' },
    { value: 'overcast', label: 'Overcast' },
  ];
  readonly staticTrackMoistureOptions: Array<{
    value: StaticWeatherTrackMoisture;
    label: string;
  }> = [
    { value: 'none', label: 'None' },
    { value: 'very_light', label: 'Very Light' },
    { value: 'light', label: 'Light' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'heavy', label: 'Heavy' },
    { value: 'very_heavy', label: 'Very Heavy' },
  ];
  readonly staticWindDirectionOptions: Array<{
    value: StaticWeatherWindDirection;
    label: string;
  }> = [
    { value: 'north', label: 'North' },
    { value: 'northeast', label: 'Northeast' },
    { value: 'east', label: 'East' },
    { value: 'southeast', label: 'Southeast' },
    { value: 'south', label: 'South' },
    { value: 'southwest', label: 'Southwest' },
    { value: 'west', label: 'West' },
    { value: 'northwest', label: 'Northwest' },
  ];

  constructor(
    private sessionService: SessionService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    // Populate form with current session data
    this.selectedTrackId = this.session.trackId;
    this.trackSearchQuery = this.session.trackName;
    this.lapsPerDriver = this.session.sessionDetails?.lapsPerDriver || 10;
    this.minIRating = this.session.minIRating;
    this.maxIRating = this.session.maxIRating;
    this.selectedCarClasses = this.session.allowedCarClasses || [];
    this.hydrateStaticWeatherConfig(this.session.staticWeatherConfig);
    this.startTime = this.toDateTimeLocal(this.session.sessionDetails.startTime);
    this.endTime = this.session.sessionDetails.endTime
      ? this.toDateTimeLocal(this.session.sessionDetails.endTime)
      : '';

    this.loadTracks();
    this.loadCarClasses();
    this.loadCars();
  }

  loadTracks(): void {
    this.apiService.getTracks().subscribe({
      next: (response) => {
        this.tracks = response.tracks;
        this.filteredTracks = response.tracks;
      },
      error: (error) => {
        console.error('Failed to load tracks:', error);
      },
    });
  }

  loadCarClasses(): void {
    this.apiService.getCarClasses().subscribe({
      next: (response) => {
        this.availableCarClasses = response.carClasses;
      },
      error: (error) => {
        console.error('Failed to load car classes:', error);
      },
    });
  }

  loadCars(): void {
    this.apiService.getCars().subscribe({
      next: (response) => {
        const sortedCars = [...response.cars].sort((a, b) =>
          this.getCarDisplayName(a).localeCompare(
            this.getCarDisplayName(b),
            undefined,
            {
              sensitivity: 'base',
            },
          ),
        );
        this.availableCars = sortedCars;
        this.filteredCars = sortedCars;
      },
      error: (error) => {
        console.error('Failed to load cars:', error);
      },
    });
  }

  filterTracks(): void {
    const query = this.trackSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredTracks = this.tracks;
      return;
    }
    this.filteredTracks = this.tracks.filter(
      (track) =>
        track.name.toLowerCase().includes(query) ||
        (track.configName && track.configName.toLowerCase().includes(query)),
    );
  }

  filterCars(): void {
    const query = this.carSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredCars = this.availableCars;
      return;
    }
    this.filteredCars = this.availableCars.filter(
      (car) =>
        car.name.toLowerCase().includes(query) ||
        (car.carName && car.carName.toLowerCase().includes(query)) ||
        car.carClasses.some(
          (cc: any) =>
            cc.carClass.name.toLowerCase().includes(query) ||
            (cc.carClass.shortName &&
              cc.carClass.shortName.toLowerCase().includes(query)),
        ),
    );
  }

  selectTrack(track: any): void {
    this.selectedTrackId = track.id;
    this.trackSearchQuery = this.getTrackDisplay(track);
    this.showTrackDropdown = false;
  }

  onTrackInputFocus(): void {
    this.showTrackDropdown = true;
    this.filterTracks();
  }

  onTrackInputBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showTrackDropdown = false;
    }, 200);
  }

  onCarInputFocus(): void {
    this.showCarDropdown = true;
    this.filterCars();
  }

  onCarInputBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showCarDropdown = false;
    }, 200);
  }

  getTrackDisplay(track: any): string {
    return track.configName
      ? `${track.name} - ${track.configName}`
      : track.name;
  }

  private getCarDisplayName(car: any): string {
    return (car.carName || car.name || '').toString();
  }

  getSelectedTrackName(): string {
    if (!this.selectedTrackId) return '';
    const track = this.tracks.find((t) => t.id === this.selectedTrackId);
    return track ? this.getTrackDisplay(track) : '';
  }

  toggleCarClass(carClassName: string): void {
    const index = this.selectedCarClasses.indexOf(carClassName);
    if (index >= 0) {
      this.selectedCarClasses.splice(index, 1);
    } else {
      this.selectedCarClasses.push(carClassName);
    }
  }

  isCarClassSelected(carClassName: string): boolean {
    return this.selectedCarClasses.includes(carClassName);
  }

  saveChanges(): void {
    if (this.saving) {
      return;
    }

    if (!this.selectedTrackId) {
      this.error = 'Please select a track';
      return;
    }

    if (this.startTime && Number.isNaN(new Date(this.startTime).getTime())) {
      this.error = 'Please provide a valid start date/time';
      return;
    }

    if (this.endTime && Number.isNaN(new Date(this.endTime).getTime())) {
      this.error = 'Please provide a valid end date/time';
      return;
    }

    if (this.startTime && this.endTime) {
      const start = new Date(this.startTime).getTime();
      const end = new Date(this.endTime).getTime();
      if (end <= start) {
        this.error = 'End date/time must be after start date/time';
        return;
      }
    }

    const normalizedMinIRating = this.normalizeOptionalRating(this.minIRating);
    const normalizedMaxIRating = this.normalizeOptionalRating(this.maxIRating);
    const staticWeatherConfig = this.enforceStaticWeather
      ? this.buildStaticWeatherConfig()
      : undefined;

    if (
      normalizedMinIRating !== undefined &&
      normalizedMaxIRating !== undefined &&
      normalizedMinIRating > normalizedMaxIRating
    ) {
      this.error =
        'Minimum iRating must be less than or equal to maximum iRating';
      return;
    }

    if (this.enforceStaticWeather && !staticWeatherConfig) {
      this.error = 'Complete all static weather fields before saving';
      return;
    }

    const trackName = this.getSelectedTrackName();

    this.error = '';
    this.saving = true;
    this.sessionService
      .editSession(
        this.session.id,
        trackName,
        this.selectedTrackId,
        this.lapsPerDriver,
        this.selectedCarClasses.length > 0
          ? this.selectedCarClasses
          : undefined,
        this.session.allowSetupChanges,
        staticWeatherConfig,
        normalizedMinIRating,
        normalizedMaxIRating,
        this.startTime ? new Date(this.startTime).toISOString() : undefined,
        this.endTime ? new Date(this.endTime).toISOString() : undefined,
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.sessionUpdated.emit();
          this.close.emit();
        },
        error: (error) => {
          this.saving = false;
          this.error = error.error?.error || 'Failed to edit session';
        },
      });
  }

  makeSessionLiveNow(): void {
    this.startTime = this.toDateTimeLocal(new Date());
    this.saveChanges();
  }

  get canMakeLiveNow(): boolean {
    if (!this.startTime) {
      return false;
    }
    return new Date(this.startTime).getTime() > Date.now();
  }

  private toDateTimeLocal(date: Date | string): string {
    const parsed = new Date(date);
    const localDate = new Date(
      parsed.getTime() - parsed.getTimezoneOffset() * 60000,
    );
    return localDate.toISOString().slice(0, 16);
  }

  cancel(): void {
    this.close.emit();
  }

  toggleStaticWeatherEnforcement(): void {
    this.enforceStaticWeather = !this.enforceStaticWeather;
    if (this.enforceStaticWeather) {
      this.hydrateStaticWeatherConfig(DEFAULT_STATIC_WEATHER_CONFIG);
    } else {
      this.hydrateStaticWeatherConfig(undefined);
    }
  }

  private normalizeOptionalRating(
    value: number | undefined,
  ): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0
      ? Math.floor(parsed)
      : undefined;
  }

  private buildStaticWeatherConfig(): StaticWeatherConfig | undefined {
    const airTempC = this.normalizeFiniteNumber(this.staticAirTempC);
    const relativeHumidityPct = this.normalizeFiniteNumber(
      this.staticRelativeHumidityPct,
    );
    const windSpeedMps = this.normalizeFiniteNumber(this.staticWindSpeedMps);

    if (
      airTempC === undefined ||
      relativeHumidityPct === undefined ||
      windSpeedMps === undefined
    ) {
      return undefined;
    }

    return {
      skies: this.staticWeatherSkies,
      trackMoisture: this.staticTrackMoisture,
      airTempC,
      relativeHumidityPct,
      windSpeedMps,
      windDirection: this.staticWindDirection,
    };
  }

  private normalizeFiniteNumber(value: number | undefined): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private hydrateStaticWeatherConfig(
    config: StaticWeatherConfig | undefined,
  ): void {
    this.enforceStaticWeather = !!config;
    const resolvedConfig = config || DEFAULT_STATIC_WEATHER_CONFIG;
    this.staticWeatherSkies = resolvedConfig.skies;
    this.staticTrackMoisture = resolvedConfig.trackMoisture;
    this.staticAirTempC = resolvedConfig.airTempC;
    this.staticRelativeHumidityPct = resolvedConfig.relativeHumidityPct;
    this.staticWindSpeedMps = resolvedConfig.windSpeedMps;
    this.staticWindDirection = resolvedConfig.windDirection;
  }

  formatTemperatureWithFahrenheit(value: number | undefined): string {
    if (value === undefined || value === null || !Number.isFinite(value)) {
      return 'C';
    }

    const fahrenheit = (value * 9) / 5 + 32;
    return `${value.toFixed(1)} C (${fahrenheit.toFixed(1)} F)`;
  }
}
